/**
 * The one bundle. Every page loads this and nothing else.
 *
 * Why one bundle and not one per page: `build/build.mjs` discovers entrypoints
 * by convention and does not enable esbuild's code splitting, so two
 * entrypoints that both import `session.ts` get two copies of it — and two
 * copies of a module holding an in-flight-mint guard is two tabs' worth of bot
 * checks from one page. A single entrypoint has no such failure. It also means
 * one cached file across the landing → queue → transcript → format sequence,
 * which is four page views of the acquisition model paying for one download.
 *
 * If it ever outgrows its budget, the fix is `splitting: true` plus a manifest
 * in `build/build.mjs` — an INFRA change, requested in comms/UI.md rather than
 * made here.
 *
 * Dispatch is by `data-page` on <body>, and every page module is a plain
 * function. No custom elements, no framework, no router.
 */

import { attachDialog } from './lib/dialog.ts';
import { data, qs, qsa } from './lib/dom.ts';
import { startPageTimer } from './lib/page-time.ts';
import { loadQuota } from './lib/quota.ts';
import { startRatingPrompt } from './lib/rating-prompt.ts';
import { hasSession, sessionToken } from './lib/session.ts';
import { mintsEarly, type PageName } from './lib/session-pages.ts';
import { mountAutosubPage } from './pages/autosub.ts';
import { mountCalculatorPage } from './pages/calculator.ts';
import { mountEditorPage } from './pages/editor.ts';
import { mountFormatPage } from './pages/format.ts';
import { mountJobPage } from './pages/job.ts';
import { mountLandingPage } from './pages/landing.ts';
import { mountLiveTranslatePage } from './pages/live-translate.ts';
import { mountMicPage } from './pages/mic.ts';
import { mountSubtitleTranslatePage } from './pages/subtitle-translate.ts';
import { mountTextPdfPage } from './pages/text-pdf.ts';
import { mountToolPage } from './pages/tool.ts';
import { mountTranscriptPage } from './pages/transcript.ts';

function pageName(): PageName {
  const value = data(document.body, 'page');
  switch (value) {
    case 'landing':
    case 'job':
    case 'transcript':
    case 'format':
    case 'tool':
    case 'editor':
    case 'autosub':
    case 'mic':
    case 'text-pdf':
    case 'subtitle-translate':
    case 'live-translate':
    case 'calculator':
      return value;
    default:
      return 'static';
  }
}

/** Tap-equivalent tooltips. No affordance on this site is hover-only. */
function wireTooltips(): void {
  for (const trigger of qsa<HTMLElement>('[aria-describedby]')) {
    const id = trigger.getAttribute('aria-describedby');
    if (!id) continue;
    const bubble = document.getElementById(id);
    if (!bubble?.classList.contains('tip__bubble')) continue;
    bubble.hidden = true;
    const show = () => {
      bubble.hidden = false;
    };
    const hide = () => {
      bubble.hidden = true;
    };
    trigger.addEventListener('pointerenter', show);
    trigger.addEventListener('focus', show);
    trigger.addEventListener('pointerleave', hide);
    trigger.addEventListener('blur', hide);
    // The tap equivalent. `click` on an aria-disabled anchor does not navigate,
    // so this is the only thing it does.
    trigger.addEventListener('click', (ev) => {
      if (trigger.getAttribute('aria-disabled') === 'true') ev.preventDefault();
      bubble.hidden = !bubble.hidden;
    });
  }
}

/** Any dialog in the page markup gets its light dismiss and focus return. */
function wireDialogs(): void {
  for (const dialog of qsa<HTMLDialogElement>('dialog')) attachDialog(dialog);
}

/**
 * The header's menus: light dismiss on top of each `<details>`.
 *
 * The element already opens, closes and takes the keyboard on its own, which is
 * why it is a `<details>` and not a scripted button — the menu works on first
 * paint, before this bundle has loaded, and it works with scripting off. What
 * `<details>` does NOT do is close when you click somewhere else or press
 * Escape, and a dropdown that stays open over the page after you have moved on
 * reads as broken.
 *
 * So this adds exactly those two behaviours and nothing else. It never opens the
 * menu, never preventDefaults a click, and a click on a link inside it is
 * untouched — the links are the point of the menu.
 */
function wireNavMenus(): void {
  const menus = qsa<HTMLDetailsElement>('[data-navmenu]');
  if (menus.length === 0) return;

  document.addEventListener('click', (ev) => {
    const target = ev.target;
    if (!(target instanceof Node)) return;
    for (const menu of menus) {
      // `contains` covers the summary itself, so a click that TOGGLES the menu
      // is never also a click that closes it.
      if (menu.open && !menu.contains(target)) menu.open = false;
    }
  });

  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape') return;
    for (const menu of menus) {
      if (!menu.open) continue;
      menu.open = false;
      // Focus goes back to the control that opened it, or it lands on <body>
      // and the next Tab starts from the top of the page.
      qs<HTMLElement>('summary', menu)?.focus();
    }
  });
}

function boot(): void {
  const page = pageName();
  // How long this page is on screen, sent when it is hidden (lib/page-time.ts). Every page.
  startPageTimer();
  // The optional rating card after a tool page's first copy or download (lib/rating-prompt.ts),
  // from the attribute itself: pageName() reads audio-out and burn, which have a tool, as static.
  startRatingPrompt(data(document.body, 'page') ?? '');
  wireDialogs();
  wireTooltips();
  wireNavMenus();

  if (mintsEarly(page, document)) {
    // At idle, not on the critical path. The token is wanted before the user's
    // first action, not before first paint.
    const mint = () => {
      if (!hasSession()) void sessionToken().catch(() => undefined);
    };
    if (typeof requestIdleCallback === 'function') requestIdleCallback(mint, { timeout: 2_000 });
    else setTimeout(mint, 500);
  }

  // A Live-first microphone page reads the meter only when Record opens (lib/mic-panel.ts), so
  // live typing alone asks our server nothing.
  if (qs('.meter') && !qs('[data-mic-defer-quota]')) void loadQuota();

  switch (page) {
    case 'landing':
      mountLandingPage();
      break;
    case 'job':
      mountJobPage();
      break;
    case 'transcript':
      mountTranscriptPage();
      break;
    case 'format':
      mountFormatPage();
      break;
    case 'tool':
      mountToolPage();
      // /video-to-text: the homepage's paste box above the drop zone, driven as it is there.
      if (qs('[data-transcribe-form]')) mountLandingPage();
      break;
    case 'editor':
      mountEditorPage();
      break;
    case 'autosub':
      mountAutosubPage();
      break;
    case 'mic':
      mountMicPage();
      break;
    case 'text-pdf':
      // /txt-to-pdf and /text-to-pdf (SEO_BRIEF_08 section 7): plain text made into a PDF here.
      mountTextPdfPage();
      break;
    case 'subtitle-translate':
      // The subtitle translator (SEO_BRIEF_09 section 2.3): read here, lines translated by the
      // server's language model, rebuilt here.
      mountSubtitleTranslatePage();
      break;
    case 'live-translate':
      // The live voice translator (SEO_BRIEF_10 section 2): speak, and read and hear the
      // translation; Speak and Conversation modes, Hold to talk where the browser can't listen.
      mountLiveTranslatePage();
      break;
    case 'calculator':
      // The two calculators (SEO_BRIEF_11 section 2.1): speaking time and timecodes, answered
      // as the visitor types.
      mountCalculatorPage();
      break;
    default:
      break;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
